import { create } from 'zustand';
import { insuranceService } from 'src/services/insurance';
import type { InsuranceProvider, InsurancePlan } from 'src/services/interfaces/insurance';

interface InsuranceState {
  providers: InsuranceProvider[];
  plans: InsurancePlan[];
  isLoading: boolean;
  error: string | null;
  getProviders: () => Promise<void>;
  createProvider: (data: InsuranceProvider) => Promise<void>;
  getProvider: (id: string) => Promise<void>;
  updateProvider: (id: string, data: InsuranceProvider) => Promise<void>;
  deleteProvider: (id: string) => Promise<void>;
  getPlans: () => Promise<void>;
  getFullPlans: () => Promise<void>;
  createPlan: (data: InsurancePlan) => Promise<void>;
  getPlan: (id: string) => Promise<void>;
  updatePlan: (id: string, data: InsurancePlan) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  getPlanFirstDocument: (idOrUid: string | number) => string | null;
}

export const useInsuranceStore = create<InsuranceState>((set, get) => ({
  providers: [],
  plans: [],
  isLoading: false,
  error: null,

  getPlanFirstDocument: (idOrUid: string | number): string | null => {
    const { plans } = get();
    const plan = plans.find((p) => p.id === idOrUid || p.uid === idOrUid);
    if (!plan || !Array.isArray(plan.documents) || plan.documents.length === 0) return null;
    const first = plan.documents[0];
    const baseUrl = import.meta.env.VITE_APP_BASE_URL as string | undefined;
    if (!first || !first.fileName || !first.path || !baseUrl) return null;
    const normalizedPath = first.path.startsWith('/') ? first.path.slice(1) : first.path;
    return `${baseUrl}${normalizedPath}/${first.fileName}`;
  },

  getProviders: async () => {
    set({ isLoading: true, error: null });
    try {
      const providers = await insuranceService.getProviders();
      set({ providers, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch providers.', isLoading: false });
    }
  },

  createProvider: async (data: InsuranceProvider) => {
    set({ isLoading: true, error: null });
    try {
      const provider = await insuranceService.createProvider(data);
      set({ providers: [...useInsuranceStore.getState().providers, provider], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to create provider.', isLoading: false });
    }
  },

  getProvider: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const provider = await insuranceService.getProvider(id);
      set({ providers: [provider], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch provider.', isLoading: false });
    }
  },

  updateProvider: async (id: string, data: InsuranceProvider) => {
    set({ isLoading: true, error: null });
    try {
      const provider = await insuranceService.updateProvider(id, data);
      set({
        providers: useInsuranceStore.getState().providers.map((item) =>
          item.uid === id ? provider : item
        ),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update provider.', isLoading: false });
    }
  },

  deleteProvider: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await insuranceService.deleteProvider(id);
      set({
        providers: useInsuranceStore.getState().providers.filter((item) => item.uid !== id),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to delete provider.', isLoading: false });
    }
  },

  getPlans: async () => {
    set({ isLoading: true, error: null });
    try {
      const plans = await insuranceService.getFullPlans();
      set({ plans, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch plans.', isLoading: false });
    }
  },

  getFullPlans: async () => {
    set({ isLoading: true, error: null });
    try {
      const plans = await insuranceService.getFullPlans();
      set({ plans, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch full plans.', isLoading: false });
    }
  },

  createPlan: async (data: InsurancePlan) => {
    set({ isLoading: true, error: null });
    try {
      const plan = await insuranceService.createPlan(data);
      set({ plans: [...useInsuranceStore.getState().plans, plan], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to create plan.', isLoading: false });
    }
  },

  getPlan: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const plan = await insuranceService.getPlan(id);
      set({ plans: [plan], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch plan.', isLoading: false });
    }
  },

  updatePlan: async (id: string, data: InsurancePlan) => {
    set({ isLoading: true, error: null });
    try {
      const plan = await insuranceService.updatePlan(id, data);
      set({
        plans: useInsuranceStore.getState().plans.map((item) => (item.uid === id ? plan : item)),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update plan.', isLoading: false });
    }
  },

  deletePlan: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await insuranceService.deletePlan(id);
      set({
        plans: useInsuranceStore.getState().plans.filter((item) => item.uid !== id),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to delete plan.', isLoading: false });
    }
  },
}));